import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from '../../routing';
import { commandCenterApi, formatCurrency } from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';
import { formatDateShort } from '../../utils/dateFormat';
import Icon from '../../components/Icon';
import ListRow from '../../components/ListRow';
import MobileSort from '../../components/MobileSort';
import MultiSelectFilter from '../../components/MultiSelectFilter';
import PeriodPicker from '../../components/PeriodPicker';
import { PRESETS_ANALYTIC, activePresetKey, minusDaysRange, readPeriodParams, withPeriodParams } from '../../utils/period';
import { usePresetPeriod } from '../../utils/usePresetPeriod';
import { AgeChip, LoadState, formatDate, openInApp, openPendingTab, useRefetchOnFocus } from './parts';
import { SortTh } from './statsParts';
import Modal from '../../components/Modal';
import { toast } from '../../components/Toast';
import CatalogConsistency from './CatalogConsistency';
import FailedSendsView from './FailedSendsView';
import DeletedDocModal from './DeletedDocModal';
import { useConfirm } from '../../utils/useConfirm';
import { track, EVENTS } from '../../utils/analytics';
import { DELETION_REASONS } from '../../utils/deletionReasons';
// The canonical list of every document type (kept in sync with the server's
// VALID_DOC_TYPES) — named for its first use, the Dokka forwarding settings.
import { DOKKA_DOC_TYPES as ALL_DOC_TYPES } from './modals';

// לטיפול — the cross-tenant work backlog, and the largest capability gain the
// console has over the old admin page. Four backlog views over one row
// skeleton, switched by ?view=: the three document queues share identical
// columns, and the unlinked-notes view adds one (the חשבונית מרכזת to link
// under) — still a filter plus a flag, not separate routes. A fifth, מחוקים,
// is the restore list for soft-deleted documents (its own rows, DeletedRows).
//
// Sorted by AGE everywhere, oldest first (only מחוקים can flip, a restore list
// is searched as often as it is worked). That is the whole point: a count
// tells you how much there is, age tells you what to do first. Note the
// unlinked view's age is measured from the NOTE'S OWN DATE, not upload time
// (see lib/ccQueues.js) — its rows run naturally older than the other tabs'.

const VIEWS = [
  { key: 'drafts',      label: 'טיוטות ממתינות' },
  { key: 'no-supplier', label: 'טיוטות ללא ספק' },
  { key: 'invalid',     label: 'מסמכים לא תקינים' },
  { key: 'unlinked',    label: 'תעודות משלוח לא מקושרות' },
  // Soft-deleted documents waiting for the purge (server/lib/softDelete.js) —
  // the only place they exist until then, and the way back from a wrong מחק.
  { key: 'deleted',     label: 'מחוקים' },
  // What email didn't deliver, and sending it again (#386). Its own rows and its
  // own fetch: see FailedSendsView.
  { key: 'failed-sends', label: 'שליחות שנכשלו' },
  // Not a queue of documents: an Excel export of catalog coding mismatches,
  // every restaurant at once (moved here from בריאות, owner 27/09/2026). Each
  // line of the file is a fix to make, which is what this page is for. It has
  // no rows to load and ignores the filters, so the page hides both for it.
  { key: 'catalog',     label: 'קונסיסטנטיות קטלוג' },
];

const EMPTY_COPY = {
  'drafts':      'אין טיוטות ממתינות — הכל נקי',
  'no-supplier': 'אין טיוטות תקועות ללא ספק',
  'invalid':     'אין מסמכים לא תקינים',
  'unlinked':    'אין תעודות משלוח לא מקושרות — הכל מקושר',
  'deleted':     'אין מסמכים מחוקים שאפשר לשחזר',
};

// מחוקים tab: who deleted it (#307). The system sets aside the second copy of
// a document at intake; the owner audits those apart from people's deletions.
const DELETED_BY_OPTIONS = ['people', 'system'];
const DELETED_BY_LABEL = { people: 'אנשים', system: 'מערכת' };
// The server's row cap for this view (server/lib/ccQueues.js DELETED_VIEW_LIMIT).
const DELETED_VIEW_LIMIT = 3000;
// The phone's order control (MobileSort): this, or ברירת מחדל = oldest first.
const DELETED_SORT_OPTIONS = [{ key: 'newest', label: 'נמחקו לאחרונה קודם' }];

const REASON_LABEL = Object.fromEntries(DELETION_REASONS.map(r => [r.key, r.label]));

// Days until the purge: the fewer, the hotter — the inverse of AgeChip.
function DaysLeftChip({ days }) {
  if (days == null) return null;
  const tone = days <= 3 ? 'hot' : days <= 7 ? 'warm' : 'cool';
  return <span className={`cc-age is-${tone}`}>{days === 0 ? 'היום' : days === 1 ? 'יום' : `${days} ימים`}</span>;
}

function reasonText(row) {
  return REASON_LABEL[row.deleted_reason] || row.deleted_reason || '—';
}

// Every row, on every view and every layout, opens THE DOCUMENT IT NAMES —
// the tenant handoff itself is parts.jsx's shared openInApp. The unlinked
// view's rows are notes, so the row opens the note; the fix happens on the
// מרכזת (linking lives on the invoice's "ערוך קישורים"), which gets its own
// explicit affordance instead of hijacking the row click — a tap target that
// says "תעודת משלוח DN-42" and opens a different document is a lie.
function openForReview(row) {
  openInApp(`/document/${row.id}`, row.restaurant_id);
}

function openForLinking(row) {
  openInApp(`/document/${row.invoice_id}`, row.restaurant_id);
}

// The tabs whose rows can be marked "טופל".
const handledViews = new Set(['invalid', 'unlinked']);

// SQLite's CURRENT_TIMESTAMP is UTC with no zone marker.
const utc = (v) => (v ? `${String(v).replace(' ', 'T')}Z` : v);

const HANDLED_COPY = {
  invalid: {
    body: 'המסמך יירד מהרשימה כאן. אצל הלקוח הוא נשאר במצב לא תקין.',
    placeholder: 'למשל: נשלחה בקשה לספק לזיכוי',
  },
  unlinked: {
    body: 'התעודה תרד מהרשימה כאן. אצל הלקוח היא נשארת לא מקושרת.',
    placeholder: 'למשל: חויבה בחשבונית רגילה, אין למה לקשר',
  },
  'failed-sends': {
    body: 'המסמך יירד מהרשימה כאן ולא יישלח להנה"ח. ההערה נשארת כאן ולא מגיעה ללקוח.',
    placeholder: 'למשל: התקופה כבר נסגרה אצל הנה"ח',
  },
};

// "טופל" on the לא תקין, unlinked and שליחות שנכשלו tabs. Hides the row from THIS
// list (and the overview tile) only — for the customer nothing changes; see
// server/lib/ccInvalidHandled.js and ccForwardFailures.js. The note is optional;
// on the first two it lands in the document's comments thread, on שליחות שנכשלו it
// stays in the console.
function HandledModal({ view, row, onDone, onClose }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await commandCenterApi.markHandled(view, row.id, note);
      toast('סומן כטופל והוסר מהרשימה');
      onDone();
    } catch (err) {
      toast(err.response?.data?.error || 'הסימון נכשל — אפשר לנסות שוב', 'error');
      setSaving(false);
    }
  };
  return (
    <Modal onClose={onClose} size={420} ariaLabel="סימון טופל">
      <h2 className="modal-title">סימון טופל</h2>
      <p className="cc-muted" style={{ marginTop: 0 }}>
        {row.doc_type} {row.doc_num} · {row.restaurant_name}
        <br />
        {HANDLED_COPY[view]?.body || HANDLED_COPY.invalid.body}
      </p>
      <form onSubmit={submit}>
        <div className="form-group">
          <label>מה נעשה? (לא חובה)</label>
          <textarea rows={3} maxLength={500} value={note} autoFocus
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
            onChange={e => setNote(e.target.value)}
            placeholder={HANDLED_COPY[view]?.placeholder || HANDLED_COPY.invalid.placeholder} />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          <button type="submit" className="btn btn-success" disabled={saving}>
            {saving ? 'שומר...' : <><Icon name="check" size={15} /> טופל</>}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// מחוקים rows. The whole row opens the read-only viewer (DeletedDocModal) —
// the document opens nowhere else until it is restored — like every other
// console row opens its document; שחזר is its own button and never the row's
// click, so a tap meant to look can't restore. The reason's note rides the
// title (hover), like the טופל note does. The נמחק ב order toggles from its
// column header; a phone has no header, so it uses MobileSort in the filter bar
// and each row prints the date, right after the days left it decides.
function DeletedRows({ rows, isMobile, onView, onRestore, restoringId, newestFirst, onToggleSort }) {
  if (isMobile) {
    return (
      <div className="m-list">
        {rows?.map(row => (
          <ListRow
            key={row.id}
            primary={`${row.doc_type} ${row.doc_num || ''}`.trim()}
            meta={
              <>
                <DaysLeftChip days={row.days_left} />
                <span>נמחק {formatDate(utc(row.deleted_at))}</span>
                <span>·</span>
                <span>{row.restaurant_name}</span>
                <span>·</span>
                <span>{row.supplier_name || 'לא זוהה ספק'}</span>
                <span>·</span>
                <span>{reasonText(row)}</span>
                {row.deleted_by_system && <><span>·</span><span>מערכת</span></>}
              </>
            }
            amount={row.total != null ? formatCurrency(row.total) : null}
            // A tap opens the viewer, whose footer holds שחזר — never a restore
            // straight from the list.
            onClick={() => onView(row)}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>ימים לשחזור</th>
            <th>מסעדה</th>
            <th>ספק</th>
            <th>סוג מסמך</th>
            <th>מספר מסמך</th>
            <th>תאריך</th>
            <th>סכום</th>
            <th>נמחק ע״י</th>
            <SortTh col="deleted_at" sort="deleted_at" ascending={!newestFirst} onSort={onToggleSort}>נמחק ב</SortTh>
            <th>סיבה</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows?.map(row => (
            <tr key={row.id} className="cc-row-link" onClick={() => onView(row)}>
              <td><DaysLeftChip days={row.days_left} /></td>
              <td style={{ fontWeight: 600 }}>{row.restaurant_name}</td>
              <td>{row.supplier_name || <span className="cc-pill bad">לא זוהה ספק</span>}</td>
              <td>{row.doc_type}</td>
              {/* Its own column: the number is what an operator matches a
                  deletion against (a duplicate, a re-upload) — and the link
                  that opens the viewer, as the row itself does. */}
              <td className="cc-num">
                <button type="button" className="cell-link"
                  onClick={(e) => { e.stopPropagation(); onView(row); }}>
                  {row.doc_num || 'צפה'}
                </button>
              </td>
              <td className="cc-muted cc-num">{row.date ? formatDateShort(row.date) : '—'}</td>
              <td className="cc-num">{row.total != null ? formatCurrency(row.total) : '—'}</td>
              <td className="cc-muted">{row.deleted_by_label || row.deleted_by || '—'}</td>
              <td className="cc-muted cc-num">{formatDate(utc(row.deleted_at))}</td>
              <td title={row.deleted_reason_note || undefined}>
                {reasonText(row)}
                {row.deleted_reason_note ? <span className="cc-muted"> · {row.deleted_reason_note}</span> : null}
              </td>
              <td>
                {/* The aria-label names the document too: every row's
                    button would otherwise read the same to a screen reader. */}
                <button className="btn btn-sm btn-success"
                  aria-label={`שחזר ${row.doc_type} ${row.doc_num || ''}`.trim()}
                  disabled={restoringId === row.id}
                  onClick={(e) => { e.stopPropagation(); onRestore(row); }}>
                  <Icon name="undo" size={14} /> לשחזר
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function QueuesPage() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [error, setError] = useState(false);
  const [handling, setHandling] = useState(null);   // the row whose "טופל" modal is open
  const [viewing, setViewing] = useState(null);     // the מחוקים row open in the viewer
  const [restoringId, setRestoringId] = useState(null);   // a restore in flight — no second one
  const [failedCount, setFailedCount] = useState(null);   // the שליחות שנכשלו tab's count, from its view
  const [failedReload, setFailedReload] = useState(0);    // bumped when טופל changes that view's rows
  const [askConfirm, confirmDialog] = useConfirm();
  const isMobile = useIsMobile();

  const view = VIEWS.some(v => v.key === params.get('view')) ? params.get('view') : 'drafts';
  // Rides the URL as a comma list, like every other filter here, so a filtered
  // queue can be pasted to someone else. Kept OUT of setView so the filter
  // survives switching tabs — "show me everything for this branch" is one
  // question asked across all three views, not three separate questions.
  const restaurantIds = useMemo(
    () => (params.get('restaurant') || '').split(',').filter(Boolean),
    [params],
  );
  // Same rules as the restaurant filter: a comma list in the URL, survives
  // switching tabs, applied server-side before the row cap. The motivating
  // question: "which חשבוניות מרכזות are sitting in לא תקין?" — the link modal
  // flags a מרכזת there when its delivery notes don't reconcile. On the
  // unlinked tab it narrows the notes themselves (those rows ARE notes).
  const docTypes = useMemo(
    () => (params.get('doc_type') || '').split(',').filter(Boolean),
    [params],
  );
  // לא תקין tab only: also list what was already marked "טופל" — the way back
  // from a mistaken click.
  const includeHandled = params.get('handled') === '1';
  // מחוקים tab only: אנשים / מערכת. Both or neither picked = everyone.
  const deletedBySel = useMemo(
    () => (params.get('deleted_by') || '').split(',').filter(v => DELETED_BY_OPTIONS.includes(v)),
    [params],
  );
  const deletedBy = deletedBySel.length === 1 ? deletedBySel[0] : null;
  // מחוקים tab only: by name, across branches. Repeated in the URL rather than a
  // comma list, because a supplier's name can hold a comma.
  const deletedSuppliers = useMemo(() => params.getAll('supplier').filter(Boolean), [params]);
  const [supplierNames, setSupplierNames] = useState([]);   // the ספק filter's options
  // מחוקים tab only: oldest deletion first unless asked otherwise.
  const deletedNewestFirst = params.get('deleted_sort') === 'newest';

  // Default scope: the last 90 days of backlog, not all of it. The queues have
  // no natural end — a stuck draft stays until someone clears it — so with no
  // bound at all this page's row cap (queueRows' limit=500) would quietly start
  // truncating the OLDEST items first on a busy day, which is exactly backwards
  // for a screen whose whole point is surfacing the oldest ones. Same control
  // as /suppliers and /documents; PRESETS_ANALYTIC (not _MONTHLY) because a
  // backlog item has no calendar-month meaning the way an approval does.
  //
  // The lazy initializer runs exactly once (React guarantees this for
  // useState, unlike useMemo, which may discard and recompute its cache), so
  // "today" can't drift the default's edges across a session left open past
  // midnight.
  const [initialRange] = useState(() => minusDaysRange(90)());

  // dateFrom/dateTo are STATE, not derived fresh from `params` each render —
  // on purpose. DayRangeView's two manual <input type="date"> fields each
  // report only THEIR OWN change and forward the other side's CURRENT value
  // unedited (see DayRangeView.jsx) — but a user can clear one of those native
  // inputs (the browser's own clear affordance, or select-all + delete) while
  // the other stays populated, calling onChange with one genuinely empty side.
  // A per-render `params.get('to') || initialRange.to` fallback would silently
  // snap that cleared field back to the default instead of leaving it open —
  // the field looks cleared but immediately reverts, and the request still
  // carries the old bound the user just removed. Verified live: clearing the
  // "from" field correctly requests `to=<date>` with no `from` at all only
  // once this is state instead of a derived fallback. Local state just holds
  // whatever DayRangeView reports, one-sided included, same as SuppliersPage
  // already does for the identical shared control.
  //
  // A preset rides the URL as `?period=<key>` and is re-evaluated to today
  // (period.js, withPeriodParams) — a refresh days later must not bring back
  // the dates "90 ימים אחרונים" meant when it was picked.
  const [dateFrom, setDateFrom] = useState(() => readPeriodParams(params, PRESETS_ANALYTIC).from || initialRange.from);
  const [dateTo, setDateTo] = useState(() => readPeriodParams(params, PRESETS_ANALYTIC).to || initialRange.to);

  const loadToken = useRef(0);
  // Flipping the order is the same rows again: the table stays on screen until
  // they arrive, so nothing flashes and the header keeps its focus.
  const keepRowsOnLoad = useRef(false);
  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setError(false);
    if (keepRowsOnLoad.current) keepRowsOnLoad.current = false; else setRows(null);
    if (view === 'catalog') return;   // an export, not a queue — nothing to fetch
    if (view === 'failed-sends') return;   // FailedSendsView fetches its own rows
    try {
      const r = await commandCenterApi.getQueue(
        view, restaurantIds, { from: dateFrom, to: dateTo }, docTypes,
        {
          includeHandled: handledViews.has(view) && includeHandled,
          deletedBy: view === 'deleted' ? deletedBy : null,
          deletedSuppliers: view === 'deleted' ? deletedSuppliers : [],
          deletedSort: view === 'deleted' && deletedNewestFirst ? 'newest' : null,
        },
      );
      if (token !== loadToken.current) return;   // superseded — see BusinessPage's loadToken
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
      if (view === 'deleted') setSupplierNames(Array.isArray(r.data?.supplier_names) ? r.data.supplier_names : []);
    } catch {
      if (token !== loadToken.current) return;
      setError(true);
      setRows(null);
    }
  }, [view, restaurantIds, dateFrom, dateTo, docTypes, includeHandled, deletedBy, deletedSuppliers, deletedNewestFirst]);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  // The customers list, not the restaurants in the current rows: options that
  // vanish as you filter are impossible to undo from the dropdown itself.
  // Demo restaurants are absent from it for the same reason they're absent
  // from the queue — filtering to one would always show nothing.
  useEffect(() => {
    commandCenterApi.getCustomers()
      .then(r => setRestaurants((r.data || []).filter(c => !c.is_demo)))
      .catch(() => setRestaurants([]));
  }, []);

  const restaurantOptions = useMemo(() => restaurants.map(r => String(r.id)), [restaurants]);
  const restaurantIdToName = useMemo(
    () => new Map(restaurants.map(r => [String(r.id), r.name])),
    [restaurants],
  );

  // A picked name stays listed when the other filters no longer reach it, or it
  // could not be unticked from the dropdown.
  const supplierOptions = useMemo(
    () => [...deletedSuppliers.filter(n => !supplierNames.includes(n)), ...supplierNames],
    [deletedSuppliers, supplierNames],
  );

  const setView = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'drafts') next.delete('view'); else next.set('view', key);
    setParams(next, { replace: true });
  };

  const setRestaurantFilter = (ids) => {
    const next = new URLSearchParams(params);
    if (ids.length) next.set('restaurant', ids.join(',')); else next.delete('restaurant');
    setParams(next, { replace: true });
  };

  const setDocTypeFilter = (types) => {
    const next = new URLSearchParams(params);
    if (types.length) next.set('doc_type', types.join(',')); else next.delete('doc_type');
    setParams(next, { replace: true });
  };

  const setDeletedByFilter = (values) => {
    const next = new URLSearchParams(params);
    if (values.length) next.set('deleted_by', values.join(',')); else next.delete('deleted_by');
    setParams(next, { replace: true });
  };

  const setSupplierFilter = (names) => {
    const next = new URLSearchParams(params);
    next.delete('supplier');
    names.forEach(name => next.append('supplier', name));
    setParams(next, { replace: true });
  };

  const setDeletedSort = (newest) => {
    if (newest === deletedNewestFirst) return;
    keepRowsOnLoad.current = true;
    const next = new URLSearchParams(params);
    if (newest) next.set('deleted_sort', 'newest'); else next.delete('deleted_sort');
    setParams(next, { replace: true });
  };

  const setIncludeHandled = (on) => {
    const next = new URLSearchParams(params);
    if (on) next.set('handled', '1'); else next.delete('handled');
    setParams(next, { replace: true });
  };

  const undoHandled = async (row) => {
    try {
      await commandCenterApi.unmarkHandled(view, row.id);
      toast('הוחזר לרשימה');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'הביטול נכשל — אפשר לנסות שוב', 'error');
    }
  };

  const clearFilters = () => {
    const next = new URLSearchParams(params);
    next.delete('restaurant');
    next.delete('doc_type');
    next.delete('deleted_by');
    next.delete('supplier');
    setParams(next, { replace: true });
  };

  // PeriodPicker can call this with EXACTLY one falsy side — a manually
  // cleared date input, see the dateFrom/dateTo state comment above — and that
  // has to land in state as-is, one-sided, not get coalesced into a full
  // range. BOTH sides falsy is different: either PeriodPicker's own "נקה
  // טווח" button, or a user clearing both manual inputs one after the other
  // (the second clear's onChange sees the first one's already-empty side).
  // Either way it means "back to the 90-day default" here, not "all time" —
  // this page has no all-time preset, and nothing unbounds queueRows' row cap.
  //
  // The default is computed afresh here rather than reusing initialRange: on a
  // page open for days, initialRange's dates are no longer "the last 90 days".
  // Every range reaching the URL write below was therefore computed just now,
  // so matching it against today's presets is sound.
  const setDateRange = (from, to) => {
    const next = (!from && !to) ? minusDaysRange(90)() : { from, to };
    setDateFrom(next.from);
    setDateTo(next.to);
    setParams(withPeriodParams(params, activePresetKey(next.from, next.to, PRESETS_ANALYTIC), next.from, next.to), { replace: true });
  };
  // Rolls a preset forward when the tab comes back after the day changed.
  const presetKey = usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, setDateRange);

  const supplierCell = (row) => (
    row.supplier_name
      ? row.supplier_name
      : <span className="cc-pill bad">לא זוהה ספק</span>
  );

  // The unlinked view adds one column (the מרכזת to link under) and one action
  // (opening it). Same page, same filter, same row skeleton — a flag, not a
  // fork.
  const unlinked = view === 'unlinked';
  const handleable = handledViews.has(view);   // has "טופל"
  const catalog = view === 'catalog';
  // The deleted view is a restore list, not a backlog: no date window (nothing
  // waits longer than 30 days), rows that don't open (the document 404s until
  // restored), and one action — שחזר.
  const deletedView = view === 'deleted';
  // Failures, not a backlog by date: no period picker, and a comment has no
  // document type, so that filter goes on its side (it would claim to narrow).
  const failedSends = view === 'failed-sends';
  const failedKind = params.get('kind') === 'comments' ? 'comments' : 'forwards';
  const showsHandledToggle = handleable || (failedSends && failedKind === 'forwards');
  const setFailedKind = (kind) => {
    const next = new URLSearchParams(params);
    if (kind === 'comments') next.set('kind', 'comments'); else next.delete('kind');
    setParams(next, { replace: true });
  };

  // From the row's שחזר or the viewer's footer; a successful restore closes
  // the viewer too — the document is no longer on this list.
  const restoreRow = (row) => askConfirm({
    title: 'שחזור מסמך',
    message: `לשחזר את "${row.doc_type}${row.doc_num ? ' · ' + row.doc_num : ''}" של ${row.restaurant_name || 'המסעדה'}?`,
    detail: 'המסמך יחזור למצב שהיה בו לפני המחיקה, עם התשלומים וההערות שלו. הוא ייפתח בלשונית חדשה.',
    confirmLabel: 'שחזר',
    tone: 'success',
    onConfirm: async () => {
      // Inside the click, before any await — see openPendingTab.
      const tab = openPendingTab();
      setRestoringId(row.id);
      try {
        const r = await commandCenterApi.restoreDeleted(row.id);
        const restored = r.data || {};
        track(EVENTS.DOCUMENT_RESTORED, {
          doc_type: row.doc_type,
          restored_as_draft: !!restored.restored_as_draft,
          days_deleted: restored.days_deleted ?? null,
        });
        // A restored מרכזת can come back with fewer delivery notes linked:
        // a note linked elsewhere meanwhile, or itself deleted, is left out.
        const dropped = restored.links_dropped > 0
          ? ` · ${restored.links_dropped === 1 ? 'קישור תעודה אחד לא שוחזר' : `${restored.links_dropped} קישורי תעודות לא שוחזרו`}`
          : '';
        toast((restored.restored_as_draft
          ? 'שוחזר כטיוטה מסומנת — בינתיים נשמר מסמך סופי עם אותו מספר'
          : 'המסמך שוחזר') + dropped);
        tab.go(`/document/${row.id}`, restored.restaurant_id ?? row.restaurant_id);
        setViewing(null);
        load();
      } catch (err) {
        tab.cancel();
        toast(err.response?.data?.error || 'השחזור נכשל — אפשר לנסות שוב', 'error');
      } finally {
        setRestoringId(null);
      }
    },
  });

  // "הכל נקי" is a claim about the WHOLE queue; it must not survive a filter
  // narrowing what's on screen, or a clean 90-day window reads as a clean
  // backlog. The 90-day default itself doesn't count as "narrowed" — that's
  // the normal view, and it should still say the queue is clean when it is.
  // By preset key, not initialRange's dates, so a rolled-forward 90 days
  // still counts as the default.
  const hasDateFilter = presetKey !== '90d' && !deletedView;
  const hasRestaurantFilter = restaurantIds.length > 0;
  const hasDocTypeFilter = docTypes.length > 0;
  const hasDeletedByFilter = deletedView && deletedBy !== null;
  const hasSupplierFilter = deletedView && deletedSuppliers.length > 0;
  const emptyMessage = hasDocTypeFilter || hasDeletedByFilter || hasSupplierFilter || (hasRestaurantFilter && hasDateFilter)
    ? 'אין פריטים בסינון שנבחר'
    : hasRestaurantFilter
    ? 'אין פריטים במסעדות שנבחרו'
    : hasDateFilter
    ? 'אין פריטים בטווח התאריכים שנבחר'
    : EMPTY_COPY[view];

  return (
    <>
      <h1 className="cc-page-title">לטיפול</h1>

      {/* Placed exactly where /suppliers and /documents put theirs: its own
          `.docs-filter-bar` ABOVE the data card, not a control inside it. The
          filter scopes every queue tab — pick the customer, then pick which of
          their backlogs — so it has to read as the outer control.

          The bar, not the wrap: `.docs-filter-wrap` is sticky at top:60px,
          an offset measured against the CUSTOMER app's navbar. The console's
          topbar is a different height, so inheriting it would pin this bar at
          the wrong place. Same pill, same position, no borrowed offset.

          Hidden on the catalog tab: that report always covers every
          restaurant, all time, every type — filters left on screen there would
          claim to narrow a file they don't touch. */}
      {!catalog && (
        <div className="cc-filter-wrap">
          <div className="docs-filter-bar">
            <MultiSelectFilter
              label="מסעדה"
              values={restaurantIds}
              options={restaurantOptions}
              renderOption={(id) => restaurantIdToName.get(id) || id}
              onChange={setRestaurantFilter}
              searchable
              minSearchChars={2}
            />
            {!(failedSends && failedKind === 'comments') && (
              <MultiSelectFilter
                label="סוג מסמך"
                values={docTypes}
                options={ALL_DOC_TYPES}
                onChange={setDocTypeFilter}
              />
            )}
            {deletedView && (
              <MultiSelectFilter
                label="ספק"
                values={deletedSuppliers}
                options={supplierOptions}
                onChange={setSupplierFilter}
                searchable
                minSearchChars={2}
              />
            )}
            {deletedView && (
              <MultiSelectFilter
                label="נמחק ע״י"
                values={deletedBySel}
                options={DELETED_BY_OPTIONS}
                renderOption={(v) => DELETED_BY_LABEL[v]}
                onChange={setDeletedByFilter}
              />
            )}
            {deletedView && isMobile && (
              <MobileSort
                options={DELETED_SORT_OPTIONS}
                sort={deletedNewestFirst ? { field: 'newest', dir: 'desc' } : { field: null, dir: null }}
                onChange={(field) => setDeletedSort(Boolean(field))}
              />
            )}
            {(hasRestaurantFilter || hasDocTypeFilter || hasDeletedByFilter || hasSupplierFilter) && (
              <button className="btn btn-sm btn-secondary" onClick={clearFilters}>
                <Icon name="close" size={12} /> נקה סינון
              </button>
            )}
            {/* .docs-filter-datewrap carries its own margin-inline-start: auto
                (index.css), so this always lands at the far end of the bar
                regardless of how many other filters sit before it. */}
            {!deletedView && !failedSends && (
              <PeriodPicker
                dateFrom={dateFrom}
                dateTo={dateTo}
                onChange={setDateRange}
                presets={PRESETS_ANALYTIC}
                variant="pill"
              />
            )}
          </div>
        </div>
      )}

      <div className="cc-card">
        <div className="view-tabs" style={{ margin: 0, flexWrap: 'wrap' }}>
          {VIEWS.map(v => (
            <button
              key={v.key}
              className={`view-tab${view === v.key ? ' is-active' : ''}`}
              onClick={() => setView(v.key)}
            >
              {v.label}{v.key === 'failed-sends'
                ? (failedSends && failedCount != null ? ` (${failedCount})` : '')
                : (rows && view === v.key ? ` (${rows.filter(r => !r.handled_at).length})` : '')}
            </button>
          ))}
        </div>
        {deletedView && rows && rows.length >= DELETED_VIEW_LIMIT && (
          <p className="cc-muted" style={{ margin: '10px 0 0' }}>
            מוצגות {DELETED_VIEW_LIMIT.toLocaleString('he-IL')} השורות הראשונות. אפשר לצמצם לפי מסעדה, ספק או נמחק ע״י.
          </p>
        )}
        {showsHandledToggle && (
          <label className="cc-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeHandled} onChange={e => setIncludeHandled(e.target.checked)} />
            {unlinked ? 'הצג גם תעודות שסומנו טופל' : 'הצג גם מסמכים שסומנו טופל'}
          </label>
        )}
      </div>

      <div className="cc-card">
        {catalog ? <CatalogConsistency /> : failedSends ? (
          <FailedSendsView
            kind={failedKind}
            onKindChange={setFailedKind}
            restaurantIds={restaurantIds}
            docTypes={docTypes}
            includeHandled={includeHandled}
            reloadKey={failedReload}
            onHandle={setHandling}
            onCount={setFailedCount}
          />
        ) : (
          <LoadState
            loading={rows === null && !error}
            error={error}
            onRetry={load}
            empty={rows?.length === 0}
            emptyMessage={emptyMessage}
          >
            {deletedView ? (
              <DeletedRows
                rows={rows}
                isMobile={isMobile}
                onView={setViewing}
                onRestore={restoreRow}
                restoringId={restoringId}
                newestFirst={deletedNewestFirst}
                onToggleSort={() => setDeletedSort(!deletedNewestFirst)}
              />
            ) : isMobile ? (
              // Below the table breakpoint the queue becomes the app's shared
              // two-line mobile row. Age leads the meta line — on a phone it is
              // the first thing that should register.
              <div className="m-list">
                {rows?.map(row => (
                  <ListRow
                    key={row.id}
                    primary={`${row.doc_type} ${row.doc_num || ''}`.trim()}
                    meta={
                      <>
                        <AgeChip days={row.age_days} />
                        <span>{row.restaurant_name}</span>
                        <span>·</span>
                        <span>{row.supplier_name || 'לא זוהה ספק'}</span>
                        {handleable && (
                          /* Same nested-button constraint as the unlinked pill below. */
                          <span
                            className={`cc-pill${row.handled_at ? ' ok' : ''}`}
                            role="button"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); if (row.handled_at) undoHandled(row); else setHandling(row); }}
                          >
                            {row.handled_at
                              ? <><Icon name="undo" size={11} /> טופל · החזר לרשימה</>
                              : <><Icon name="check" size={11} /> טופל</>}
                          </span>
                        )}
                        {unlinked && (
                          /* The row opens the NOTE it names, like every row in
                             the console — so the invoice needs its own tap
                             target here, or a phone user could never reach the
                             place linking actually happens. A span, not a
                             button: ListRow's root is already a <button> and
                             nested buttons are invalid HTML. */
                          <span
                            className="cc-pill"
                            role="button"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); openForLinking(row); }}
                          >
                            <Icon name="external" size={11} /> לקישור: {row.invoice_doc_num || '—'}
                          </span>
                        )}
                      </>
                    }
                    amount={row.total != null ? formatCurrency(row.total) : null}
                    onClick={() => openForReview(row)}
                  />
                ))}
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>גיל</th>
                      <th>מסעדה</th>
                      <th>ספק</th>
                      <th>מסמך</th>
                      <th>תאריך</th>
                      <th>סכום</th>
                      {unlinked && <th>חשבונית מרכזת לקישור</th>}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows?.map(row => (
                      // The whole row opens the document, like every other table
                      // in the console. Previously only the small button acted,
                      // so clicking the row — the obvious target — did nothing.
                      <tr key={row.id} className="cc-row-link" onClick={() => openForReview(row)}>
                        <td><AgeChip days={row.age_days} /></td>
                        <td style={{ fontWeight: 600 }}>{row.restaurant_name}</td>
                        <td>{supplierCell(row)}</td>
                        <td>
                          {row.doc_type} {row.doc_num}
                          {row.duplicate_warning ? (
                            <span className="cc-pill warn" style={{ marginInlineStart: 6 }}>כפילות</span>
                          ) : null}
                          {row.handled_at ? (
                            <span className="cc-pill ok" style={{ marginInlineStart: 6 }}
                              title={[row.handled_by, row.handled_note].filter(Boolean).join(' · ')}>
                              טופל {formatDate(utc(row.handled_at))}
                            </span>
                          ) : null}
                        </td>
                        <td className="cc-muted cc-num">{row.date || '—'}</td>
                        <td className="cc-num">{row.total != null ? formatCurrency(row.total) : '—'}</td>
                        {unlinked && (
                          <td>
                            {row.invoice_doc_num || '—'}
                            {row.invoice_date ? <span className="cc-muted"> · {formatDateShort(row.invoice_date)}</span> : null}
                          </td>
                        )}
                        <td>
                          {/* No bulk approve here, deliberately: approve-all is
                              tenant-scoped by design, and a cross-tenant version
                              is one click from mass-mutating customer financial
                              data from a console. Approving stays inside the
                              tenant, where the operator can see what they sign.
                              Same logic for linking: no bulk-link, the operator
                              opens the מרכזת and chooses which notes it covers.

                              ONE review button for every view (it mirrors the
                              row click); the unlinked view adds the link action
                              in front of it. */}
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            {handleable && (row.handled_at ? (
                              <button className="btn btn-sm btn-secondary"
                                onClick={(e) => { e.stopPropagation(); undoHandled(row); }}>
                                <Icon name="undo" size={13} /> החזר לרשימה
                              </button>
                            ) : (
                              <button className="btn btn-sm btn-success"
                                onClick={(e) => { e.stopPropagation(); setHandling(row); }}>
                                <Icon name="check" size={13} /> טופל
                              </button>
                            ))}
                            {unlinked && (
                              <button className="btn btn-sm btn-primary"
                                onClick={(e) => { e.stopPropagation(); openForLinking(row); }}>
                                <Icon name="external" size={13} /> פתח לקישור
                              </button>
                            )}
                            <button className="btn btn-sm btn-secondary"
                              onClick={(e) => { e.stopPropagation(); openForReview(row); }}>
                              <Icon name="external" size={13} /> פתח לבדיקה
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </LoadState>
        )}
      </div>

      {viewing && (
        <DeletedDocModal
          row={viewing}
          reasonText={reasonText}
          DaysLeftChip={DaysLeftChip}
          onRestore={restoreRow}
          restoring={restoringId === viewing.id}
          onClose={() => setViewing(null)}
          onGone={load}
        />
      )}
      {confirmDialog}
      {handling && (
        <HandledModal
          view={view}
          row={handling}
          onClose={() => setHandling(null)}
          onDone={() => { setHandling(null); load(); setFailedReload(n => n + 1); }}
        />
      )}
    </>
  );
}
